import React from 'react';
import { RegulatedBehavioursSection } from './sections/RegulatedBehavioursSection';
import { PreventionStrategiesSection } from './sections/PreventionStrategiesSection';
import { AnxietyBehavioursSection } from './sections/AnxietyBehavioursSection';
import { SupportStrategiesSection } from './sections/SupportStrategiesSection';
import { DefensiveBehavioursSection } from './sections/DefensiveBehavioursSection';
import { DeescalationStrategiesSection } from './sections/DeescalationStrategiesSection';
import { RiskBehavioursSection } from './sections/RiskBehavioursSection';
import { SafetyInterventionsSection } from './sections/SafetyInterventionsSection';
import { DeescalationSignsSection } from './sections/DeescalationSignsSection';
import { PostDeescalationStaffResponsesSection } from './sections/PostDeescalationStaffResponsesSection';
import { PostConflictRestorationSection } from './sections/PostConflictRestorationSection';
import { DocumentDownload } from '../../document-download/DocumentDownload';
import { BehaviourResponsePlanData } from '../../../models/form-types';
import { useDocumentForm } from '../../../hooks/useDocumentForm';
import { useDocumentDownload } from '../../../hooks/useDocumentDownload';
import { useUsage } from '../../../hooks/useUsage';
import { submitBehaviourResponsePlan } from '../../../services/document.service';
import { DocumentResponse } from '../../../models/api-response';
import { UsageIndicator } from '../../common/UsageIndicator';
import { MissingFieldsSummary, MissingField } from '../../common/MissingFieldsSummary';

interface BehaviourResponsePlanFormProps {
  onBack?: () => void;
}

// Initial form data
const initialFormData: BehaviourResponsePlanData = {
  regulatedBehaviours: [],
  regulatedBehavioursOther: '',
  regulatedBehavioursStrategies: [],
  regulatedBehavioursStrategiesOther: '',
  anxietyBehaviours: [],
  anxietyBehavioursOther: '',
  anxietyBehavioursStrategies: [],
  anxietyBehavioursStrategiesOther: '',
  defensiveBehaviours: [],
  defensiveBehavioursOther: '',
  defensiveBehavioursStrategies: [],
  defensiveBehavioursStrategiesOther: '',
  riskBehaviours: [],
  riskBehavioursOther: '',
  riskBehavioursStrategies: [],
  riskBehavioursStrategiesOther: '',
  signsOfDeescalation: [],
  signsOfDeescalationOther: '',
  signsOfDeescalationStrategies: [],
  signsOfDeescalationStrategiesOther: '',
  relationshipReestablishment: [],
  relationshipReestablishmentOther: '',
};

export const BehaviourResponsePlanForm: React.FC<BehaviourResponsePlanFormProps> = ({ onBack }) => {
  // Usage tracking hook
  const { getUsageForProduct, loading: usageLoading, refresh: refreshUsage } = useUsage();
  const usage = getUsageForProduct('personalised_plan');
  const canUse = usage?.can_use ?? false;

  // Document download functionality
  const { isDownloadVisible, documentId, base64Content, showDownload, hideDownload } = useDocumentDownload();

  // Form management and submission
  const {
    formState,
    updateArrayField,
    handleTextChange,
    submitForm,
    isSubmitting
  } = useDocumentForm<BehaviourResponsePlanData, DocumentResponse>({
    initialData: initialFormData,
    submitService: submitBehaviourResponsePlan,
    onSubmitSuccess: (response: DocumentResponse) => {
      showDownload(response);
      // Refresh usage after successful generation
      refreshUsage();
    }
  });

  const { formData } = formState;

  // Helper function to handle checkbox changes for array fields
  const handleCheckboxChange = (field: keyof BehaviourResponsePlanData, value: string, checked: boolean) => {
    const currentArray = (formData[field] as string[]) || [];
    const newArray = checked 
      ? [...currentArray, value]
      : currentArray.filter(item => item !== value);
    updateArrayField(field, newArray);
  };

  // Check if a section has at least one checkbox selected OR something in the "other" field
  const isSectionValid = (checkboxArray: string[], otherField: string) => {
    return checkboxArray.length > 0 || otherField.trim() !== '';
  };

  // Validate all required sections
  const formValidation = {
    isRegulatedBehavioursValid: isSectionValid(formData.regulatedBehaviours, formData.regulatedBehavioursOther),
    isRegulatedStrategiesValid: isSectionValid(formData.regulatedBehavioursStrategies, formData.regulatedBehavioursStrategiesOther),
    isAnxietyBehavioursValid: isSectionValid(formData.anxietyBehaviours, formData.anxietyBehavioursOther),
    isAnxietyStrategiesValid: isSectionValid(formData.anxietyBehavioursStrategies, formData.anxietyBehavioursStrategiesOther),
    isDefensiveBehavioursValid: isSectionValid(formData.defensiveBehaviours, formData.defensiveBehavioursOther),
    isDefensiveStrategiesValid: isSectionValid(formData.defensiveBehavioursStrategies, formData.defensiveBehavioursStrategiesOther),
    isRiskBehavioursValid: isSectionValid(formData.riskBehaviours, formData.riskBehavioursOther),
    isRiskStrategiesValid: isSectionValid(formData.riskBehavioursStrategies, formData.riskBehavioursStrategiesOther),
    isDeescalationSignsValid: isSectionValid(formData.signsOfDeescalation, formData.signsOfDeescalationOther),
    isDeescalationStrategiesValid: isSectionValid(formData.signsOfDeescalationStrategies, formData.signsOfDeescalationStrategiesOther),
    isRelationshipReestablishmentValid: isSectionValid(formData.relationshipReestablishment, formData.relationshipReestablishmentOther),
  };

  // Check if all required sections are valid
  const isFormValid = 
    formValidation.isRegulatedBehavioursValid &&
    formValidation.isRegulatedStrategiesValid &&
    formValidation.isAnxietyBehavioursValid &&
    formValidation.isAnxietyStrategiesValid &&
    formValidation.isDefensiveBehavioursValid &&
    formValidation.isDefensiveStrategiesValid &&
    formValidation.isRiskBehavioursValid &&
    formValidation.isRiskStrategiesValid &&
    formValidation.isDeescalationSignsValid &&
    formValidation.isDeescalationStrategiesValid &&
    formValidation.isRelationshipReestablishmentValid;

  // Get missing sections for the unified summary banner
  const getMissingFields = (): MissingField[] => {
    const missing: MissingField[] = [];
    if (!formValidation.isRegulatedBehavioursValid) missing.push({ label: 'Regulated behaviours', anchorId: 'brp-section-regulated' });
    if (!formValidation.isRegulatedStrategiesValid) missing.push({ label: 'Prevention strategies', anchorId: 'brp-section-regulated' });
    if (!formValidation.isAnxietyBehavioursValid) missing.push({ label: 'Anxiety behaviours', anchorId: 'brp-section-anxiety' });
    if (!formValidation.isAnxietyStrategiesValid) missing.push({ label: 'Support strategies', anchorId: 'brp-section-anxiety' });
    if (!formValidation.isDefensiveBehavioursValid) missing.push({ label: 'Defensive behaviours', anchorId: 'brp-section-defensive' });
    if (!formValidation.isDefensiveStrategiesValid) missing.push({ label: 'De-escalation strategies', anchorId: 'brp-section-defensive' });
    if (!formValidation.isRiskBehavioursValid) missing.push({ label: 'Risk behaviours', anchorId: 'brp-section-risk' });
    if (!formValidation.isRiskStrategiesValid) missing.push({ label: 'Safety interventions', anchorId: 'brp-section-risk' });
    if (!formValidation.isDeescalationSignsValid) missing.push({ label: 'Signs of de-escalation', anchorId: 'brp-section-deescalation' });
    if (!formValidation.isDeescalationStrategiesValid) missing.push({ label: 'Post de-escalation staff responses', anchorId: 'brp-section-deescalation' });
    if (!formValidation.isRelationshipReestablishmentValid) missing.push({ label: 'Relationship re-establishment', anchorId: 'brp-section-relationship' });
    return missing;
  };

  // Handler functions for form sections
  const handleRegulatedCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('regulatedBehaviours', value, checked);
  };
  const handleRegulatedOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'regulatedBehavioursOther';
    handleTextChange(e);
  };

  const handleRegulatedStrategiesCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('regulatedBehavioursStrategies', value, checked);
  };
  const handleRegulatedStrategiesOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'regulatedBehavioursStrategiesOther';
    handleTextChange(e);
  };

  const handleAnxietyCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('anxietyBehaviours', value, checked);
  };
  const handleAnxietyOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'anxietyBehavioursOther';
    handleTextChange(e);
  };

  const handleAnxietyStrategiesCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('anxietyBehavioursStrategies', value, checked);
  };
  const handleAnxietyStrategiesOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'anxietyBehavioursStrategiesOther';
    handleTextChange(e);
  };

  const handleDefensiveCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('defensiveBehaviours', value, checked);
  };
  const handleDefensiveOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'defensiveBehavioursOther';
    handleTextChange(e);
  };

  const handleDefensiveStrategiesCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('defensiveBehavioursStrategies', value, checked);
  };
  const handleDefensiveStrategiesOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'defensiveBehavioursStrategiesOther';
    handleTextChange(e);
  };

  const handleRiskCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('riskBehaviours', value, checked);
  };
  const handleRiskOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'riskBehavioursOther';
    handleTextChange(e);
  };

  const handleRiskStrategiesCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('riskBehavioursStrategies', value, checked);
  };
  const handleRiskStrategiesOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'riskBehavioursStrategiesOther';
    handleTextChange(e);
  };

  const handleSignsOfDeescalationCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('signsOfDeescalation', value, checked);
  };
  const handleSignsOfDeescalationOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'signsOfDeescalationOther';
    handleTextChange(e);
  };

  const handleSignsOfDeescalationStrategiesCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('signsOfDeescalationStrategies', value, checked);
  };
  const handleSignsOfDeescalationStrategiesOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'signsOfDeescalationStrategiesOther';
    handleTextChange(e);
  };

  const handleRelationshipReestablishmentCheckbox = (value: string, checked: boolean) => {
    handleCheckboxChange('relationshipReestablishment', value, checked);
  };
  const handleRelationshipReestablishmentOther = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.name = 'relationshipReestablishmentOther';
    handleTextChange(e);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitForm();
  };

  const missingFields = getMissingFields();

  return (
    <form onSubmit={handleSubmit} className="brp-form-container">
      <div className="bsp-form-header">
        <h1>Create a Behaviour Response Plan (BRP)</h1>
        <p className="form-description">
          Provide consistent, safe, and supportive strategies for responding to challenging behaviours to prevent escalation and promote positive outcomes.
        </p>
      </div>


      <div id="brp-section-regulated">
        <div className="brp-section-heading brp-heading-green">Regulated behaviours</div>
        <RegulatedBehavioursSection
          values={formData.regulatedBehaviours}
          otherValue={formData.regulatedBehavioursOther}
          onCheckboxChange={handleRegulatedCheckbox}
          onOtherChange={handleRegulatedOther}
          className="brp-outline-green"
        />
        <PreventionStrategiesSection
          values={formData.regulatedBehavioursStrategies}
          otherValue={formData.regulatedBehavioursStrategiesOther}
          onCheckboxChange={handleRegulatedStrategiesCheckbox}
          onOtherChange={handleRegulatedStrategiesOther}
          className="brp-outline-green"
        />
      </div>

      <div id="brp-section-anxiety">
        <div className="brp-section-heading brp-heading-yellow">Anxiety behaviours</div>
        <AnxietyBehavioursSection
          values={formData.anxietyBehaviours}
          otherValue={formData.anxietyBehavioursOther}
          onCheckboxChange={handleAnxietyCheckbox}
          onOtherChange={handleAnxietyOther}
          className="brp-outline-yellow"
        />
        <SupportStrategiesSection
          values={formData.anxietyBehavioursStrategies}
          otherValue={formData.anxietyBehavioursStrategiesOther}
          onCheckboxChange={handleAnxietyStrategiesCheckbox}
          onOtherChange={handleAnxietyStrategiesOther}
          className="brp-outline-yellow"
        />
      </div>

      <div id="brp-section-defensive">
        <div className="brp-section-heading brp-heading-orange">Defensive behaviours</div>
        <DefensiveBehavioursSection
          values={formData.defensiveBehaviours}
          otherValue={formData.defensiveBehavioursOther}
          onCheckboxChange={handleDefensiveCheckbox}
          onOtherChange={handleDefensiveOther}
          className="brp-outline-orange"
        />
        <DeescalationStrategiesSection
          values={formData.defensiveBehavioursStrategies}
          otherValue={formData.defensiveBehavioursStrategiesOther}
          onCheckboxChange={handleDefensiveStrategiesCheckbox}
          onOtherChange={handleDefensiveStrategiesOther}
          className="brp-outline-orange"
        />
      </div>

      <div id="brp-section-risk">
        <div className="brp-section-heading brp-heading-red">Risk behaviours</div>
        <RiskBehavioursSection
          values={formData.riskBehaviours}
          otherValue={formData.riskBehavioursOther}
          onCheckboxChange={handleRiskCheckbox}
          onOtherChange={handleRiskOther}
          className="brp-outline-red"
        />
        <SafetyInterventionsSection
          values={formData.riskBehavioursStrategies}
          otherValue={formData.riskBehavioursStrategiesOther}
          onCheckboxChange={handleRiskStrategiesCheckbox}
          onOtherChange={handleRiskStrategiesOther}
          className="brp-outline-red"
        />
      </div>

      <div id="brp-section-deescalation">
        <div className="brp-section-heading brp-heading-purple">Signs of de-escalation</div>
        <DeescalationSignsSection
          values={formData.signsOfDeescalation}
          otherValue={formData.signsOfDeescalationOther}
          onCheckboxChange={handleSignsOfDeescalationCheckbox}
          onOtherChange={handleSignsOfDeescalationOther}
          className="brp-outline-purple"
        />
        <PostDeescalationStaffResponsesSection
          values={formData.signsOfDeescalationStrategies}
          otherValue={formData.signsOfDeescalationStrategiesOther}
          onCheckboxChange={handleSignsOfDeescalationStrategiesCheckbox}
          onOtherChange={handleSignsOfDeescalationStrategiesOther}
          className="brp-outline-purple"
        />
      </div>

      <div id="brp-section-relationship">
        <div className="brp-section-heading brp-heading-blue">Relationship re-establishment</div>
        <PostConflictRestorationSection
          values={formData.relationshipReestablishment}
          otherValue={formData.relationshipReestablishmentOther}
          onCheckboxChange={handleRelationshipReestablishmentCheckbox}
          onOtherChange={handleRelationshipReestablishmentOther}
          className="brp-outline-blue"
        />
      </div>

      {/* Info box and Submit/Navigation Buttons */}
      <div className="form-group">
        <div className="form-info-box">
          <p className="info-primary">Gymea documents are all editable Word docs, so you can make changes to wording, fonts or appearance.</p>
          <p className="info-secondary">P.S. Opening your document on mobile devices might cause fonts or formatting to display differently to how they will appear when you open in the Word&nbsp;app.</p>
        </div>
      </div>

      <MissingFieldsSummary missingFields={missingFields} />

      {/* Submit and Navigation Buttons */}
      <div className="form-actions">
        {onBack && (
          <button type="button" onClick={onBack} className="secondary-button">
            Back
          </button>
        )}
        <button
          type="submit"
          className="primary-button"
          disabled={isSubmitting || !isFormValid || !canUse}
        >
          {isSubmitting ? 'Submitting...' : 'Draft a plan'}
        </button>
      </div>

      {/* Usage Indicator - shown below form actions */}
      {!isDownloadVisible && (
        <div className="usage-indicators-container">
          {usageLoading ? (
            <UsageIndicator
              productName="Personalised Plan"
              used={0}
              limit={null}
              remaining={null}
              canUse={false}
              period={new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long' })}
              loading={true}
            />
          ) : usage ? (
            <UsageIndicator
              productName={usage.name}
              used={usage.used}
              limit={usage.limit}
              remaining={usage.remaining}
              canUse={usage.can_use}
              period={new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long' })}
              loading={false}
            />
          ) : (
            <div className="usage-loading">
              <p>Loading usage information...</p>
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {formState.submitError && (
        <div className="error-message">
          <p>Error: {formState.submitError}</p>
        </div>
      )}

      {/* Document Download */}
      {isDownloadVisible && documentId && base64Content && (
        <DocumentDownload
          documentId={documentId}
          base64Content={base64Content}
          documentType="behaviour-response-plan"
          onClose={hideDownload}
        />
      )}
    </form>
  );
}; 